import type { TFunction } from "i18next";

/**
 * The windows the panel can be scoped to. These mirror `EVENT_JOB_WINDOWS` in
 * `@nexus/db` — the API rejects an hour count that is not one of them — but are
 * declared here rather than imported, because the frontend must not depend on a
 * backend package.
 */
export const EVENT_TIME_RANGES = [
  { value: "1h", hours: 1 },
  { value: "6h", hours: 6 },
  { value: "24h", hours: 24 },
  { value: "7d", hours: 24 * 7 },
  { value: "30d", hours: 24 * 30 },
] as const;

export type EventTimeRange = (typeof EVENT_TIME_RANGES)[number]["value"];

export const EVENT_TIME_RANGE_VALUES = EVENT_TIME_RANGES.map((range) => range.value) as [
  EventTimeRange,
  ...EventTimeRange[],
];

/** Wide enough to hold a quiet night, narrow enough that a bad hour still shows. */
export const EVENT_TIME_RANGE_DEFAULT: EventTimeRange = "24h";
const DEFAULT_RANGE_HOURS = 24;

export function eventTimeRangeHours(range: EventTimeRange): number {
  return (
    EVENT_TIME_RANGES.find((candidate) => candidate.value === range)?.hours ??
    DEFAULT_RANGE_HOURS
  );
}

/**
 * The reverse mapping, for the stored default window (which the ops settings
 * keep in hours). An hour count the panel does not offer falls back to the
 * default range rather than throwing — same forgiveness as the API's own
 * `resolveEventJobWindow`.
 */
export function eventTimeRangeFromHours(hours: number): EventTimeRange {
  return (
    EVENT_TIME_RANGES.find((candidate) => candidate.hours === hours)?.value ??
    EVENT_TIME_RANGE_DEFAULT
  );
}

/** The next window out, or null at the widest one the panel offers. */
export function widerEventTimeRange(range: EventTimeRange): EventTimeRange | null {
  const index = EVENT_TIME_RANGES.findIndex((candidate) => candidate.value === range);
  return EVENT_TIME_RANGES[index + 1]?.value ?? null;
}

const RANGE_LABEL_FALLBACK: Record<EventTimeRange, string> = {
  "1h": "Last hour",
  "6h": "Last 6 hours",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
};

/** Labels are user-facing and module scope cannot call `useTranslation`. */
export function eventTimeRangeLabel(range: EventTimeRange, t: TFunction): string {
  return t(`admin.events.filters.timeRange.${range}`, RANGE_LABEL_FALLBACK[range]);
}

/**
 * The compact form, for the segmented control and anywhere the window sits next
 * to numbers and has to stay out of their way. A day is only a clearer unit than
 * hours once there is more than one of them — "1d" reads as a rounding of
 * something, where "24h" is exact.
 */
export function formatEventWindowShort(windowHours: number, t: TFunction): string {
  if (windowHours > 24 && windowHours % 24 === 0) {
    return t("admin.events.filters.windowDays", "{{count}}d", { count: windowHours / 24 });
  }
  return t("admin.events.filters.windowHours", "{{count}}h", { count: windowHours });
}
